// Calls list — classification filters, tag filter, review/transcript toggles, advanced CSV export, settings.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Settings2 } from "lucide-react";
import api from "../../lib/api";
import { loadCallConfig } from "./CallClassifyPanel";

export function useCallConfig() {
  const [cfg, setCfg] = useState(null);
  const [tags, setTags] = useState([]);
  useEffect(() => {
    loadCallConfig().then(setCfg).catch(() => {});
    api.get("/calls/tags/all").then(({ data }) => setTags(data.tags || [])).catch(() => {});
  }, []);
  return { cfg, tags, setCfg };
}

export function clsParam(clsSel) {
  return Object.entries(clsSel).filter(([, v]) => v.length).map(([k, v]) => `${k}:${v.join("|")}`).join(";");
}

function Toggle({ on, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="t2-mono"
      style={{ padding: "7px 11px", fontSize: 10, textTransform: "uppercase", letterSpacing: ".1em", borderRadius: 8,
        border: "1px solid var(--t2-border2)", background: on ? "var(--t2-orange-dim)" : "var(--t2-surface)",
        color: on ? "var(--t2-orange)" : "var(--t2-grey2)", cursor: "pointer" }}>{children}</button>
  );
}

export function CallClassFilters({ MultiSelect, cfg, tagList, clsSel, setClsSel, tagSel, setTagSel, review, setReview, transcript, setTranscript }) {
  if (!cfg) return null;
  const tagOpts = tagList.map((t) => t.tag.replace(/^ai:/, "")).filter((t, i, a) => a.indexOf(t) === i).slice(0, 80);
  return (
    <>
      {cfg.fields.map((f) => (
        <MultiSelect key={f.key} label={f.label} options={f.options} selected={clsSel[f.key] || []}
          onChange={(v) => setClsSel({ ...clsSel, [f.key]: v })} facets={null} testid={`cls-${f.key}`} />
      ))}
      <MultiSelect label="Tags" options={tagOpts} selected={tagSel} onChange={setTagSel} facets={null} testid="cls-tags" />
      <Toggle on={review} onClick={() => setReview(!review)} testid="calls-needs-review">Needs review</Toggle>
      <Toggle on={transcript === "yes"} onClick={() => setTranscript(transcript === "yes" ? "" : "yes")} testid="calls-has-transcript">Has transcript</Toggle>
    </>
  );
}

export function ExportCsvButton({ buildParams }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const p = buildParams(1); p.delete("page"); p.delete("limit");
      const { data } = await api.get(`/calls/export.csv?${p.toString()}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `talmia-calls-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Export failed"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} className="t2-btn-ghost" style={{ fontSize: 11 }} data-testid="calls-export-csv">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export CSV
    </button>
  );
}

export function SettingsButton({ onClick }) {
  return (
    <button type="button" onClick={onClick} className="t2-btn-ghost" style={{ fontSize: 11 }} data-testid="calls-classification-settings">
      <Settings2 className="w-3.5 h-3.5" /> Classification settings
    </button>
  );
}
